import React from 'react';
import { gql } from '@apollo/client';
import { useQuery } from 'hooks/apollo';
import { FormattedMessage } from 'react-intl';
import { RESULTS, GRADE, COLOR, BENCHMARK } from 'shared/constants/questionCategories';
import useFormatMessage from 'hooks/useFormatMessage';
import { Form, Input, InputNumber, Select, Checkbox, Row, Col } from 'antd';

import useUser from 'hooks/useUser';
import globalMessages from 'translations/messages';
import { BENCHMARK_CATEGORIES } from 'shared/constants/benchmark';
import { QuestionSubType, QuestionType, SurveyType } from 'shared/prisma/types';
import messages from './messages';
import questionMessages from '../EditStructure/DragToAddContent/messages';

const QUESTION_CATEGORIES = gql`
  query { questionCategories { id title } }
`;

const { Option } = Select;

function getCategories(categories, titles, trim = false) {
  if (!categories || !categories.length) { return []; }

  const filtered = titles.map(title => categories.find(cat => title === cat.title)).filter(x => x);

  return filtered.map(cat => {
    let { title } = cat;
    if (trim) {
      const titleParts = cat.title.split(' - ');
      title = titleParts[titleParts.length - 1];
    }
    return {
      id: cat.id,
      title,
    };
  });
}

const interchangableTypes = [QuestionType.Checkbox, QuestionType.Radio, QuestionType.Grade];

export const questionTabs = Object.values(RESULTS.SURVEY);
export const questionTypes = [GRADE.GENERIC, COLOR.BY_INDEX, BENCHMARK.EXCLUDE];

const EditQuestionSettings = ({ survey, type }) => {
  const { isSuperUser } = useUser();
  const $t = useFormatMessage();
  const { loading: loadingCategories, data: { questionCategories = [] } = {} } = useQuery(QUESTION_CATEGORIES, { fetchPolicy: 'cache-first' });
  const tabs = getCategories(questionCategories, questionTabs, true);
  const types = getCategories(questionCategories, questionTypes);
  const benchmarks = getCategories(questionCategories, BENCHMARK_CATEGORIES);

  const canChangeType = interchangableTypes.includes(type);

  return (
    <>
      {type === QuestionType.Field && (
      <Row>
        <Col>
          <Form.Item name="subType" label={<FormattedMessage {...messages.subType} />}>
            <Select>
              {Object.values(QuestionSubType).map(st => (
                <Option key={st} value={st}><FormattedMessage {...messages[st]} /></Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col>
          <Form.Item noStyle shouldUpdate={(prevValues, curValues) => prevValues.subType !== curValues.subType}>
            {({ getFieldValue }) => (
              <Form.Item style={{ marginLeft: 32 }} name="rows" label={<FormattedMessage {...messages.rowCount} />}>
                <InputNumber disabled={getFieldValue('subType') !== QuestionSubType.Textarea} />
              </Form.Item>
            )}
          </Form.Item>
        </Col>
        <Col>
          <Form.Item name="max" label={<FormattedMessage {...messages.maxLength} />}>
            <InputNumber placeholder={1000} min={100} max={9999} />
          </Form.Item>
        </Col>
      </Row>
      )}

      <Row gutter={16}>
        <Col>
          <Form.Item name="required" valuePropName="checked">
            <Checkbox><FormattedMessage {...messages.required} /></Checkbox>
          </Form.Item>
        </Col>

        {[QuestionType.Checkbox, QuestionType.Radio].includes(type) && (
          <>
            <Col>
              <Form.Item name="random" valuePropName="checked">
                <Checkbox><FormattedMessage {...messages.randomize} /></Checkbox>
              </Form.Item>
            </Col>
            <Col>
              <Form.Item noStyle shouldUpdate={(prevValues, curValues) => prevValues.random !== curValues.random}>
                {({ getFieldValue }) => (
                  <Form.Item
                    name="lockLastOption"
                    valuePropName="checked"
                  >
                    <Checkbox disabled={!getFieldValue('random')}><FormattedMessage {...messages.lockLastOption} /></Checkbox>
                  </Form.Item>
                )}
              </Form.Item>
            </Col>
          </>
        )}
      </Row>

      {type === QuestionType.Checkbox && (
        <Row gutter={16}>
          <Col>
            <Form.Item name="min" label={<FormattedMessage {...messages.minAnswers} />}>
              <InputNumber />
            </Form.Item>
          </Col>
          <Col>
            <Form.Item name="max" label={<FormattedMessage {...messages.maxAnswers} />}>
              <InputNumber />
            </Form.Item>
          </Col>
        </Row>
      )}

      {type === QuestionType.Contact && (
      <Form.Item name="email" label="Contact Email">
        <Input type="email" />
      </Form.Item>
      )}

      <Row gutter={16}>
        <Col>
          <Form.Item name="type" label={<FormattedMessage {...messages.type} />}>
            <Select disabled={!canChangeType}>
              {(canChangeType ? interchangableTypes : Object.values(QuestionType)).map(t => (
                <Option key={t} value={t}><FormattedMessage {...questionMessages[t.toLowerCase()]} /></Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        {survey.type !== SurveyType.Feedback && (
        <Col>
          <Form.Item
            name="tab"
            label={<FormattedMessage {...messages.resultsCollection} />}
            rules={[{ required: true, message: $t(messages.resultsCollectionRequired) }]}
          >
            <Select loading={loadingCategories}>
              {tabs.map(cat => <Option key={cat.id} value={cat.id}>{cat.title}</Option>)}
            </Select>
          </Form.Item>
        </Col>
        )}

        <Col>
          <Form.Item name="benchmark" label={<FormattedMessage {...globalMessages.benchmark} />}>
            <Select
              loading={loadingCategories}
              allowClear
              disabled={!isSuperUser}
            >
              {benchmarks.map(cat => <Option key={cat.id} value={cat.id}>{cat.title}</Option>)}
            </Select>
          </Form.Item>
        </Col>
        <Col flex={1}>
          <Form.Item name="types" label={<FormattedMessage {...messages.questionTags} />}>
            <Select
              loading={loadingCategories}
              mode="multiple"
              allowClear
              disabled={!isSuperUser}
            >
              {types.map(cat => <Option key={cat.id} value={cat.id}>{cat.title}</Option>)}
            </Select>
          </Form.Item>
        </Col>
      </Row>

    </>
  );
};

export default EditQuestionSettings;
